import { useEffect, useRef, useState } from 'react';
import { IsearchAddressResult } from '@optimus/models';
import debounce from 'lodash/debounce';
import { searchAddressesByQueryText } from '../api/dashboardAPI';
import { Logger } from '../config/logging/logger';

export const MAX_QUERY_LENGTH = 200;

export const useAddressAutocomplete = () => {
  const [suggestions, setSuggestions] = useState<IsearchAddressResult[]>([]);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const MAX_RESULT_COUNT = 5;
  const DEBOUNCE_TIMEOUT = 500;
  const logger = Logger.create('useAddressAutocomplete');

  const debouncedFn = useRef(
    debounce(async (queryText) => {
      try {
        const response = await searchAddressesByQueryText({
          queryText,
          maxResultCount: MAX_RESULT_COUNT,
        });

        // Removes duplicates based on fullAddress
        const uniqueResults = Array.from(
          new Map((response as IsearchAddressResult[])
            ?.map((item) => [item.fullAddress, item])).values(),
        );

        setSuggestions(uniqueResults);
      } catch (error) {
        logger.error(Logger.format('Error fetching address suggestions:', error));
      } finally {
        setIsFetchingSuggestions(false);
      }
    }, DEBOUNCE_TIMEOUT),
  );

  // Optional: cleanup on unmount to cancel any pending debounce calls
  useEffect(() => () => {
    debouncedFn.current.cancel();
  }, []);

  const fetchSuggestions = async (queryText: string) => {
    if (!queryText) {
      setSuggestions([]);
      return;
    }

    // Truncate queryText to MAX_QUERY_LENGTH characters
    const truncatedQueryText = queryText.slice(0, MAX_QUERY_LENGTH);

    setIsFetchingSuggestions(true);
    debouncedFn.current(truncatedQueryText);
  };

  return { suggestions, fetchSuggestions, isFetchingSuggestions };
};
